import { useEffect, useRef, useState } from "react";

export type WsStatus = "connected" | "connecting" | "disconnected";

export function useWebSocketReconnect(
  url: string,
  {
    onMessage,
    onOpen,
    maxBackoffMs = 30_000,
    enabled = true,
  }: {
    onMessage: (event: MessageEvent) => void;
    onOpen?: () => void;
    maxBackoffMs?: number;
    enabled?: boolean;
  },
): { status: WsStatus; send: (data: string) => void } {
  const [status, setStatus] = useState<WsStatus>("disconnected");
  const wsRef = useRef<WebSocket | null>(null);
  const backoffRef = useRef(1_000);
  const reconnectTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const unmounted = useRef(false);
  const onMessageRef = useRef(onMessage);
  onMessageRef.current = onMessage;
  const onOpenRef = useRef(onOpen);
  onOpenRef.current = onOpen;

  function connect() {
    if (unmounted.current) return;
    setStatus("connecting");
    const ws = new WebSocket(url);
    wsRef.current = ws;

    ws.onopen = () => {
      if (unmounted.current) return ws.close();
      backoffRef.current = 1_000;
      setStatus("connected");
      onOpenRef.current?.();
    };

    ws.onmessage = (evt) => onMessageRef.current(evt);

    ws.onclose = () => {
      if (unmounted.current) return;
      setStatus("disconnected");
      reconnectTimer.current = setTimeout(() => {
        backoffRef.current = Math.min(backoffRef.current * 2, maxBackoffMs);
        connect();
      }, backoffRef.current);
    };

    ws.onerror = () => ws.close();
  }

  useEffect(() => {
    if (!enabled) {
      setStatus("disconnected");
      return;
    }
    unmounted.current = false;
    connect();
    return () => {
      unmounted.current = true;
      if (reconnectTimer.current) clearTimeout(reconnectTimer.current);
      wsRef.current?.close();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled]);

  function send(data: string) {
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      wsRef.current.send(data);
    }
  }

  return { status, send };
}
